import {
  DonutChart,
  HorizontalBarChart,
  HorizontalBarChartWithAxis,
  LineChart,
  ResponsiveContainer,
  VerticalBarChart,
  VerticalStackedBarChart,
  type ChartAnnotation,
  type ChartDataPoint,
  type HorizontalBarChartStyles,
  type HorizontalBarChartWithAxisDataPoint,
  type LineChartPoints,
  type ResponsiveChildProps,
  type VerticalBarChartDataPoint,
  type VerticalStackedChartProps,
} from "@fluentui/react-charts";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactElement, type RefObject } from "react";
import { SERIES } from "./chart-colors";

/*
 * 차트 래퍼. 화면은 이 파일의 작은 데이터 모양만 알고, 그리는 일은 여기서만 한다.
 * 선·막대·파이·스파크는 Fluent UI Charts(v9). 면적 스택·요일 히트맵·단계 레인은 Fluent에 없어서 여기서 그린다.
 * 어댑터(report-charts, task-charts, forecast-model)는 이 모양을 만드는 순수 함수라 bun:test로 검증한다.
 */

const useStyles = makeStyles({
  lineFrame: { position: "relative", width: "100%" },
  lineGuide: { position: "absolute", left: 0, top: 0, pointerEvents: "none" },
  spark: { display: "block", width: "100%", minWidth: 0, lineHeight: 0, overflow: "hidden" },
  // 축·눈금·격자는 숨기고 선만 남긴다(Fluent 차트의 공개 클래스 이름).
  sparkInner: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    "& .fui-cart__xAxis, & .fui-cart__yAxis, & .fui-chart__xAxis, & .fui-chart__yAxis": { display: "none" },
    "& svg text": { display: "none" },
  },
  hidden: { display: "none" },
  /** Fluent 차트 안의 글자(축·막대 값·콜아웃·범례)도 앱 규칙대로 Regular 400으로 맞춘다. */
  chartText: {
    width: "100%",
    minWidth: 0,
    "& *": { fontWeight: tokens.fontWeightRegular },
    // Fluent 축 글자는 10px Semibold(caption2Strong). 11px Regular·보조색으로 물려 데이터가 먼저 보이게 한다.
    "& svg text": { fontWeight: tokens.fontWeightRegular, fontSize: "11px", fill: tokens.colorNeutralForeground2 },
    // 콜아웃 값은 인라인으로 Hero(28px)·시리즈 색·Bold가 박힌다. 축·범례가 이미 읽히므로 왼쪽 정렬 본문 한 줄로 덮는다.
    "& .fui-PopoverSurface": {
      padding: "6px 8px",
      minWidth: "0",
    },
    "& .fui-cart__calloutContentRoot": { textAlign: "left" },
    "& .fui-cart__calloutContentY": {
      fontSize: `${tokens.fontSizeBase300} !important`,
      lineHeight: `${tokens.lineHeightBase300} !important`,
      fontWeight: `${tokens.fontWeightRegular} !important`,
      color: `${tokens.colorNeutralForeground1} !important`,
    },
    "& .fui-cart__calloutlegendText": { marginBottom: "0 !important", textAlign: "left" },
    "& .fui-cart__calloutInfoContainer": { marginTop: "2px !important" },
    // 이름과 값을 한 줄에 둔다. Fluent는 이름 아래 값을 따로 쌓고 위에 13px를 더한다.
    "& .fui-cart__calloutBlockContainer": {
      display: "flex",
      flexDirection: "row",
      alignItems: "baseline",
      columnGap: "6px",
      marginTop: "0 !important",
      textAlign: "left",
    },
  },
  /** 막대 차트 비교 선. Fluent는 실선·3px만 그리므로 기준선은 점선으로 낮춘다. */
  referenceLine: {
    "& svg path[fill='transparent']": {
      strokeDasharray: "2 3",
      strokeWidth: "1.5px",
    },
  },
  /** 누적 막대가 카드 안쪽 여백을 넘지 않게 자른다 */
  clip: { overflow: "hidden" },
  stackWrap: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, minWidth: 0 },
  legend: { display: "flex", flexWrap: "wrap", gap: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`, margin: 0, padding: 0, listStyle: "none" },
  pieStack: { display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0, width: "100%" },
  pieGrow: { flex: "1 1 auto", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, width: "100%" },
  pieBox: { flex: "0 0 auto", lineHeight: 0 },
  pieHit: { "& path": { cursor: "pointer" } },
  legendCenter: { justifyContent: "center", width: "100%" },
  callout: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightRegular, whiteSpace: "nowrap" },
  areaRoot: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, minWidth: 0, width: "100%" },
  areaPlot: {
    position: "relative",
    width: "100%",
    minWidth: 0,
    borderRadius: tokens.borderRadiusMedium,
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "2px" },
  },
  areaSvg: { display: "block", width: "100%", height: "100%" },
  areaPill: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    padding: "1px 8px",
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
  areaDate: {
    position: "absolute",
    transform: "translateX(-50%)",
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "24px",
    padding: `0 ${tokens.spacingHorizontalS}`,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
  },
  legendButton: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, color: tokens.colorNeutralForeground1 },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
    ":disabled": { cursor: "default", opacity: 0.5 },
  },
  legendDot: { width: "8px", height: "8px", borderRadius: tokens.borderRadiusCircular, flexShrink: 0 },
  heatRoot: { width: "100%", minWidth: 0 },
  heatGrid: { width: "100%", minWidth: 0 },
  heatHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    fontVariantNumeric: "tabular-nums",
  },
  heatHeadOn: { color: tokens.colorBrandForeground1 },
  heatRowLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 0,
    paddingRight: "4px",
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  heatCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    minHeight: "22px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    font: "inherit",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    cursor: "pointer",
    "&[data-level='0']": { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2 },
    "&[data-level='1']": { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorNeutralForeground1 },
    "&[data-level='2']": { backgroundColor: tokens.colorBrandBackground2Hover, color: tokens.colorNeutralForeground1 },
    "&[data-level='3']": { backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand },
    "&[data-level='4']": { backgroundColor: tokens.colorBrandBackgroundPressed, color: tokens.colorNeutralForegroundOnBrand },
    ":hover": { outline: `1px solid ${tokens.colorNeutralStroke1}`, outlineOffset: "-1px" },
    ":active": { outline: `1px solid ${tokens.colorNeutralStroke1Hover}`, outlineOffset: "-1px" },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  heatStatic: { cursor: "default", ":hover": { outline: "none" } },
  heatGap: {
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    cursor: "default",
    ":hover": { outline: "none" },
  },
  heatMark: { boxShadow: `inset 0 0 0 1.5px ${tokens.colorStrokeFocus2}` },
  laneRoot: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, width: "100%", minWidth: 0, height: "100%" },
  /** 이름 칸은 가장 긴 글자만큼만. 160px를 넘으면 말줄임. 막대·눈금이 이름 바로 옆에서 시작한다. */
  laneFrame: { flex: "1 1 auto", display: "grid", gridTemplateColumns: "fit-content(160px) minmax(0, 1fr)", columnGap: "8px", minHeight: 0, minWidth: 0 },
  laneOverlay: { position: "relative", pointerEvents: "none", zIndex: 1 },
  laneName: {
    alignSelf: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    maxWidth: "160px",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  laneTrack: {
    alignSelf: "center",
    position: "relative",
    height: "22px",
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: "transparent",
    padding: 0,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  laneTrackStatic: { cursor: "default", ":hover": { backgroundColor: "transparent" } },
  laneBar: { position: "absolute", top: "2px", height: "18px", borderRadius: tokens.borderRadiusSmall, display: "flex", overflow: "hidden", minWidth: "8px" },
  laneBefore: { height: "100%", backgroundColor: tokens.colorBrandBackground },
  laneAfter: { height: "100%", backgroundColor: tokens.colorPaletteRedBackground3 },
  laneAxis: { position: "relative", height: "16px" },
  laneTick: {
    position: "absolute",
    top: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  laneGuide: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 0,
    borderLeft: `1px dashed ${tokens.colorNeutralForeground3}`,
    pointerEvents: "none",
  },
  laneGuideLabel: {
    position: "absolute",
    top: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    lineHeight: "14px",
    whiteSpace: "nowrap",
    backgroundColor: tokens.colorNeutralBackground1,
    padding: "0 4px",
  },
  legendValue: { color: tokens.colorNeutralForeground3, fontVariantNumeric: "tabular-nums" },
  bulletRoot: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, minWidth: 0, width: "100%" },
  bulletRow: {
    display: "grid",
    gridTemplateColumns: "minmax(84px, 1.1fr) minmax(0, 2fr) auto",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
    minHeight: "28px",
    padding: `2px ${tokens.spacingHorizontalXS}`,
    margin: `0 -${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: "none",
    background: "transparent",
    font: "inherit",
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    width: `calc(100% + ${tokens.spacingHorizontalXS} * 2)`,
    boxSizing: "border-box",
  },
  bulletRowInteractive: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  bulletName: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  bulletCode: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
    flexShrink: 0,
  },
  bulletLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  bulletTrack: {
    position: "relative",
    height: "10px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground4,
    overflow: "hidden",
  },
  bulletPlanZone: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundImage: `repeating-linear-gradient(-45deg, ${tokens.colorNeutralStroke1}, ${tokens.colorNeutralStroke1} 2px, transparent 2px, transparent 5px)`,
    opacity: 0.55,
  },
  bulletFill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: tokens.borderRadiusSmall,
  },
  bulletMarker: {
    position: "absolute",
    top: "-1px",
    bottom: "-1px",
    width: "2px",
    backgroundColor: tokens.colorNeutralForeground2,
  },
  bulletNums: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "4px",
    fontVariantNumeric: "tabular-nums",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: "nowrap",
    minWidth: "84px",
    justifyContent: "flex-end",
  },
  bulletActual: { color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300 },
  bulletPlan: { color: tokens.colorNeutralForeground3 },
});

/** Fluent 축 차트 틀: 너비를 따라가고(ResponsiveContainer) 글자 굵기를 앱 규칙에 맞춘다. */
function ChartFrame({ height, className, children }: { height: number; className?: string; children: ReactElement<ResponsiveChildProps> }) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.chartText, className)}>
      <ResponsiveContainer height={height}>{children}</ResponsiveContainer>
    </div>
  );
}

/** 0부터 max까지 4~5칸으로 나누는 보기 좋은 눈금(25·50·100 단위). */
export function niceTicks(max: number, target = 5): number[] {
  const steps = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
  const step = steps.find((s) => max / s <= target) ?? Math.ceil(max / target);
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  return ticks;
}

/* ------------------------------------------------------------------------------------------------
 * Sparkline — KPI 칸 안의 추이(비교 정보). 게이지·장식 바가 아니다.
 * ---------------------------------------------------------------------------------------------- */

export interface SparkDatum {
  x: number;
  y: number;
  label?: string;
}

export interface SparkVisualData {
  points: readonly SparkDatum[];
  color?: string;
}

/**
 * 스파크라인 y 범위: 0을 포함한다(작은 변화를 과장하지 않는다). 음수(당겨진 일정)가 있으면 아래로 넓힌다.
 * 값이 모두 같으면 위로 1만큼 열어 평평한 선이 바닥에 붙지 않게 한다.
 */
export function sparkDomain(points: readonly SparkDatum[]): { min: number; max: number } {
  const ys = points.map((p) => p.y);
  const min = Math.min(0, ...ys);
  const max = Math.max(...ys, 0);
  return { min, max: max > min ? max : min + 1 };
}

/**
 * KPI 칸의 추이. Fluent LineChart를 축·범례 없이 작게 쓴다.
 * (Fluent `Sparkline`은 React 19 StrictMode의 이중 렌더에서 축 스케일을 잃고 깨지므로 쓰지 않는다.)
 * 그림은 wrapper의 role="img" 한 개로 읽히고, 안쪽은 inert라 KPI 버튼 안에 포커스 요소가 생기지 않는다.
 * 값은 title(가리키면 보이는 툴팁)과 aria-label로 준다.
 */
export function SparkVisual({ data, ariaLabel, height = 28 }: { data: SparkVisualData; ariaLabel: string; height?: number }) {
  const styles = useStyles();
  const domain = sparkDomain(data.points);
  const color = data.color ?? SERIES.actual;
  if (data.points.length < 2) return null;
  return (
    <div className={styles.spark} style={{ height, maxHeight: height }} role="img" aria-label={ariaLabel} title={ariaLabel}>
      <div inert className={styles.sparkInner}>
        <ResponsiveContainer height={height}>
          <LineChart
            data={{ lineChartData: [{ legend: ariaLabel, color, hideNonActiveDots: true, lineOptions: { strokeWidth: 2 }, data: data.points.map((p) => ({ x: p.x, y: p.y })) }] }}
            hideLegend
            hideTooltip
            yMinValue={domain.min}
            yMaxValue={domain.max}
            margins={{ top: 3, right: 3, bottom: 3, left: 3 }}
          />
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Line — 기간 추이(진척·SPI·소진율). x는 시점 순번, 눈금 글자는 labels.
 * ---------------------------------------------------------------------------------------------- */

export interface LineSeries {
  key: string;
  legend: string;
  color: string;
  values: readonly (number | null)[];
  /** 콜아웃에 쓸 값 문구(없으면 formatY) */
  valueLabels?: readonly (string | undefined)[];
  /** 계획처럼 비교용 선은 점선 */
  dashed?: boolean;
  /** 판단 기준선(SPI 1.0 등): 콜아웃·점 없이 선만 긋는다 */
  reference?: boolean;
}

export interface TrendLineVisualProps {
  labels: readonly string[];
  /** 콜아웃 머리(기간 이름). 없으면 labels */
  calloutTitles?: readonly string[];
  series: readonly LineSeries[];
  height: number;
  ariaLabel: string;
  yMin?: number;
  yMax?: number;
  /** y 눈금을 직접 준다(없으면 Fluent가 4칸으로 나눈다) */
  yTicks?: number[];
  formatY?: (value: number) => string;
  highlightIndex?: number | null;
  /** 같은 x에서 두 y를 잇는 세로선. 계획 누적의 기준일처럼, 선과 점을 한 날에 묶을 때 */
  guide?: { index: number; fromY: number; toY: number } | null;
  onPointClick?: (index: number) => void;
}

/** LineChart에 넘기는 여백. 세로 안내선도 이 값으로 자리를 잡는다. */
const LINE_MARGINS = { top: 12, right: 16, bottom: 28, left: 44 };

export function TrendLineVisual({ labels, calloutTitles, series, height, ariaLabel, yMin, yMax, yTicks, formatY = (v) => String(v), highlightIndex, guide, onPointClick }: TrendLineVisualProps) {
  const styles = useStyles();
  const frameRef = useRef<HTMLDivElement>(null);
  const [guideLine, setGuideLine] = useState<GuideLineBox | null>(null);
  const guideIndex = guide?.index;
  const guideFrom = guide?.fromY;
  const guideTo = guide?.toY;
  useLayoutEffect(() => {
    const root = frameRef.current;
    if (!root || guideIndex === undefined || guideFrom === undefined || guideTo === undefined || yMin === undefined || yMax === undefined || yMax <= yMin) {
      setGuideLine(null);
      return;
    }
    const measure = () => {
      const svg = root.querySelector("svg");
      const x = svg ? tickX(svg, guideIndex) : null;
      if (!svg || x === null) return;
      const svgHeight = svg.clientHeight;
      const plotH = svgHeight - LINE_MARGINS.top - LINE_MARGINS.bottom;
      if (plotH <= 0) return;
      const yOf = (value: number) => LINE_MARGINS.top + (1 - (value - yMin) / (yMax - yMin)) * plotH;
      const next = { width: svg.clientWidth, svgHeight, x, y0: yOf(guideFrom), y1: yOf(guideTo) };
      setGuideLine((prev) => (prev && prev.width === next.width && prev.svgHeight === next.svgHeight && prev.x === next.x && prev.y0 === next.y0 && prev.y1 === next.y1 ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    const mutations = new MutationObserver(measure);
    mutations.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, [guideIndex, guideFrom, guideTo, yMin, yMax, height, labels.length]);
  const lineChartData: LineChartPoints[] = series.map((s) => ({
    legend: s.legend,
    color: s.color,
    legendShape: s.dashed || s.reference ? "dottedLine" : "default",
    lineOptions: s.dashed || s.reference ? { strokeDasharray: s.reference ? "2 3" : "5 3", strokeWidth: s.reference ? 1 : 1.5 } : { strokeWidth: 2 },
    hideNonActiveDots: s.reference,
    data: s.values.flatMap((v, i) =>
      v === null
        ? []
        : [
            {
              x: i,
              y: v,
              xAxisCalloutData: calloutTitles?.[i] ?? labels[i],
              yAxisCalloutData: s.valueLabels?.[i] ?? formatY(v),
              hideCallout: s.reference,
              onDataPointClick: onPointClick && !s.reference ? () => onPointClick(i) : undefined,
            },
          ],
    ),
  }));
  // 교차 강조: 고른 시점의 첫 시리즈 값 위에 값 태그를 단다(표 행을 가리키면 네 차트가 같은 시점을 가리킨다).
  const primary = series.find((s) => !s.reference);
  const hv = primary && highlightIndex !== null && highlightIndex !== undefined && highlightIndex >= 0 ? primary.values[highlightIndex] : null;
  const annotations: ChartAnnotation[] =
    primary && hv !== null && hv !== undefined
      ? [
          {
            id: "highlight",
            text: primary.valueLabels?.[highlightIndex!] ?? formatY(hv),
            coordinates: { type: "data", x: highlightIndex!, y: hv },
            layout: { align: "center", verticalAlign: "bottom", offsetY: -8, clipToBounds: true },
            style: { textColor: tokens.colorBrandForeground1, backgroundColor: tokens.colorNeutralBackground1, borderColor: tokens.colorBrandStroke1, borderWidth: 1, borderRadius: 4, fontSize: "11px", fontWeight: tokens.fontWeightRegular, padding: "0 6px" },
            connector: { strokeColor: tokens.colorBrandStroke1, strokeWidth: 1, endPadding: 2, arrow: "none" },
          },
        ]
      : [];
  return (
    <div ref={frameRef} className={styles.lineFrame}>
    <ChartFrame height={height}>
      <LineChart
        data={{ chartTitle: ariaLabel, lineChartData }}
        annotations={annotations}
        tickValues={labels.map((_, i) => i)}
        tickFormat={(v) => labels[Number(v)] ?? ""}
        xMinValue={-0.25}
        xMaxValue={Math.max(labels.length - 1, 0) + 0.25}
        yMinValue={yMin}
        yMaxValue={yMax}
        yAxisTickCount={4}
        yAxisTickValues={yTicks}
        yAxisTickFormat={(v: number) => formatY(v)}
        margins={LINE_MARGINS}
        legendProps={{ allowFocusOnLegends: true }}
        svgProps={{ "aria-label": ariaLabel }}
      />
    </ChartFrame>
    {guideLine && (
      <svg className={styles.lineGuide} width={guideLine.width} height={guideLine.svgHeight} aria-hidden>
        <line x1={guideLine.x} x2={guideLine.x} y1={guideLine.y0} y2={guideLine.y1} stroke={tokens.colorNeutralForeground2} strokeWidth={1.5} />
      </svg>
    )}
    </div>
  );
}

interface GuideLineBox {
  width: number;
  svgHeight: number;
  x: number;
  y0: number;
  y1: number;
}

/** x 눈금은 `translate(x, 0)`. 축 여백이 눈금 글자 너비만큼 늘어나므로, 안내선 x는 그 눈금에서 읽는다. */
function tickX(svg: SVGSVGElement, index: number): number | null {
  const ticks = [...svg.querySelectorAll("g.tick")].filter((el) => /translate\([^,]+,\s*0(?:\.0+)?\)/.test(el.getAttribute("transform") ?? ""));
  const match = /translate\(\s*([0-9.eE+-]+)/.exec(ticks[index]?.getAttribute("transform") ?? "");
  return match ? Number(match[1]) : null;
}

/* ------------------------------------------------------------------------------------------------
 * Columns — 시점별 수량·편차(부호 있는 막대 포함).
 * ---------------------------------------------------------------------------------------------- */

export interface ColumnDatum {
  key: string;
  label: string;
  value: number;
  valueLabel?: string;
  calloutTitle?: string;
  /** 색의 의미(범례). 같은 legend는 한 번만 표시된다 */
  legend?: string;
  color?: string;
  onClick?: () => void;
}

/** 막대 위의 비교 선(하루 평균·비슷한 주). 값은 data와 같은 순서다. */
export interface ColumnReference {
  legend: string;
  values: readonly number[];
  /** 선 콜아웃. 없으면 눈금 숫자를 그대로 읽는다. */
  valueLabel?: string;
}

export function ColumnsVisual({
  data,
  height,
  ariaLabel,
  showLegend = false,
  formatY = (v) => String(v),
  yMin,
  yMax,
  yTicks,
  reference,
  hideBarLabels = false,
  showCallout = false,
  rotateLabels = false,
}: {
  data: readonly ColumnDatum[];
  height: number;
  ariaLabel: string;
  showLegend?: boolean;
  formatY?: (value: number) => string;
  yMin?: number;
  yMax?: number;
  yTicks?: number[];
  reference?: ColumnReference;
  /** 막대 위 글자를 끈다. 달이 많아 겹칠 때. 값은 콜아웃·표에 둔다. */
  hideBarLabels?: boolean;
  /** 막대에 값을 찍지 않을 때 콜아웃으로 그 달의 금액을 보여 준다. */
  showCallout?: boolean;
  /** 눈금 글자가 겹치면 기울인다. */
  rotateLabels?: boolean;
}) {
  const styles = useStyles();
  const points: VerticalBarChartDataPoint[] = data.map((d, i) => ({
    x: d.label,
    y: d.value,
    legend: d.legend ?? d.label,
    color: d.color ?? SERIES.actual,
    xAxisCalloutData: d.calloutTitle ?? d.label,
    yAxisCalloutData: d.valueLabel ?? formatY(d.value),
    barLabel: d.valueLabel ?? formatY(d.value),
    onClick: d.onClick,
    // Fluent는 y가 0이면 그 점을 선에서 빼 이웃끼리 잇는다. 0은 축 위에 남긴다.
    lineData: reference
      ? { y: reference.values[i] || Number.EPSILON, yAxisCalloutData: reference.valueLabel ?? formatY(reference.values[i] ?? 0) }
      : undefined,
  }));
  return (
    <ChartFrame height={height} className={reference ? styles.referenceLine : undefined}>
      <VerticalBarChart
        data={points}
        chartTitle={ariaLabel}
        hideLegend={!showLegend && !reference}
        // 막대 위에 값이 이미 있으면 콜아웃은 같은 숫자를 한 번 더 연다. 글자를 숨긴 차트만 콜아웃을 연다.
        hideTooltip={!showCallout}
        hideLabels={hideBarLabels}
        rotateXAxisLables={rotateLabels}
        roundCorners
        barWidth="auto"
        maxBarWidth={28}
        yMinValue={yMin}
        yMaxValue={yMax}
        yAxisTickCount={4}
        yAxisTickValues={yTicks}
        yAxisTickFormat={(v: number) => formatY(v)}
        // 기울인 눈금은 bottom을 한 번 더 빼 그림이 납작해진다. 그때는 여백을 거의 두지 않는다.
        margins={{ top: 16, right: 16, bottom: rotateLabels ? 12 : 28, left: 48 }}
        legendProps={{ allowFocusOnLegends: true }}
        lineLegendText={reference?.legend}
        lineLegendColor={SERIES.plan}
        svgProps={{ "aria-label": ariaLabel }}
      />
    </ChartFrame>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Bars — 항목별 수량 비교(가로 막대 + 항목 축). 막대를 누르면 드릴스루.
 * ---------------------------------------------------------------------------------------------- */

export interface BarDatum<Key extends string = string> {
  key: Key;
  label: string;
  value: number;
  valueLabel?: string;
  color?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

const BAR_ROW = 30;
const BAR_CHROME = 48;

/** 가로 막대 차트 높이: 항목 수에 맞춰 늘린다(축·여백 포함). */
export function axisBarsHeight(rowCount: number): number {
  return Math.max(120, BAR_CHROME + Math.max(0, rowCount) * BAR_ROW);
}

/** 카드용 밀집 막대. 값은 막대 옆 글자(16px 미만이면 Fluent가 숨긴다)를 유지하고, 줄 간격만 좁힌다. */
const DENSE_BAR_HEIGHT = 16;
const DENSE_Y_PADDING = 0.2;
const DENSE_MARGIN_TOP = 4;
const DENSE_MARGIN_BOTTOM = 22;
const DENSE_DOMAIN = 8;

/** `BarsVisual` `dense`가 항목을 겹치지 않게 담는 높이. */
export function denseBarsHeight(rowCount: number): number {
  const n = Math.max(1, rowCount);
  const gapRate = DENSE_Y_PADDING / (1 - DENSE_Y_PADDING);
  const units = n + (n - 1) * gapRate;
  return Math.ceil(DENSE_MARGIN_TOP + DENSE_MARGIN_BOTTOM + DENSE_DOMAIN * 2 + units * DENSE_BAR_HEIGHT);
}

let measureCtx: CanvasRenderingContext2D | null | undefined;
/** 항목 축 글자 폭(px). 축 글자는 chartText에서 11px Regular로 그리므로 같은 글꼴로 잰다. */
function labelWidth(labels: readonly string[]): number {
  if (typeof document === "undefined") return 80;
  if (measureCtx === undefined) measureCtx = document.createElement("canvas").getContext("2d");
  if (!measureCtx) return 80;
  measureCtx.font = `400 11px ${getComputedStyle(document.body).fontFamily}`;
  return Math.ceil(Math.max(0, ...labels.map((l) => measureCtx!.measureText(l).width)));
}

export function BarsVisual({ data, height, ariaLabel, dense = false, formatX = (v) => String(v) }: { data: readonly BarDatum[]; height: number; ariaLabel: string; dense?: boolean; formatX?: (value: number) => string }) {
  const max = Math.max(4, ...data.map((d) => d.value));
  // 항목 이름이 잘리지 않게 왼쪽 여백을 가장 긴 이름에 맞춘다(최대 160px, 넘으면 말줄임+툴팁은 표로 보기에서).
  const left = Math.min(160, labelWidth(data.map((d) => d.label)) + 20);
  // Fluent는 마지막 항목을 맨 위에 그린다. 목록 순서(첫 항목이 위)를 지키려고 뒤집어 넘긴다.
  const points: HorizontalBarChartWithAxisDataPoint[] = [...data].reverse().map((d) => ({
    y: d.label,
    x: d.value,
    legend: d.label,
    color: d.color ?? SERIES.actual,
    // 콜아웃은 항목과 값만. 다음 행동 문장은 차트 설명에 두고 여기 반복하지 않는다.
    yAxisCalloutData: d.label,
    xAxisCalloutData: d.valueLabel ?? String(d.value),
    barLabel: d.valueLabel ?? String(d.value),
    onClick: d.onClick,
    callOutAccessibilityData: { ariaLabel: d.ariaLabel ?? `${d.label} ${d.valueLabel ?? d.value}` },
  }));
  return (
    <ChartFrame height={height}>
      <HorizontalBarChartWithAxis
        data={points}
        chartTitle={ariaLabel}
        hideLegend
        roundCorners
        barHeight={DENSE_BAR_HEIGHT}
        yAxisPadding={dense ? DENSE_Y_PADDING : undefined}
        tickValues={niceTicks(max, 4)}
        tickFormat={(v) => formatX(Number(v))}
        xMaxValue={niceTicks(max, 4).at(-1)}
        margins={dense ? { top: DENSE_MARGIN_TOP, right: 36, bottom: DENSE_MARGIN_BOTTOM, left } : { top: 8, right: 40, bottom: 28, left }}
        svgProps={{ "aria-label": ariaLabel }}
      />
    </ChartFrame>
  );
}

/* ------------------------------------------------------------------------------------------------
 * StackedBar — 전체를 이루는 구성(상태 분포). 범례를 누르면 그 구성으로 드릴스루.
 * ---------------------------------------------------------------------------------------------- */

export interface Segment<Key extends string = string> {
  key: Key;
  label: string;
  value: number;
  valueLabel?: string;
  color: string;
}

function segmentShare(segments: readonly Segment[]): { total: number; pct: (value: number) => number } {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  return { total, pct: (value) => (total ? Math.round((value / total) * 100) : 0) };
}

function SegmentLegend<Key extends string>({ segments, ariaLabel, onPick, pickHint, center = false }: { segments: readonly Segment<Key>[]; ariaLabel: string; onPick?: (key: Key) => void; pickHint: string; center?: boolean }) {
  const styles = useStyles();
  const { pct } = segmentShare(segments);
  return (
    <ul className={mergeClasses(styles.legend, center && styles.legendCenter)} aria-label={ariaLabel}>
      {segments.map((s) => {
        const content = (
          <>
            <span className={styles.legendDot} style={{ backgroundColor: s.color }} aria-hidden />
            <span>{s.label}</span>
            <span className={styles.legendValue}>
              {s.valueLabel ?? s.value} · {pct(s.value)}%
            </span>
          </>
        );
        return (
          <li key={s.key}>
            {onPick ? (
              <button type="button" className={mergeClasses(styles.legendItem, styles.legendButton)} onClick={() => onPick(s.key)} disabled={s.value === 0} aria-label={`${s.label} ${s.valueLabel ?? `${s.value}개`} ${pickHint}`}>
                {content}
              </button>
            ) : (
              <span className={styles.legendItem}>{content}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function StackedBar<Key extends string>({ segments, onPick, ariaLabel, pickHint = "보기" }: { segments: readonly Segment<Key>[]; onPick?: (key: Key) => void; ariaLabel: string; pickHint?: string }) {
  const styles = useStyles();
  const { total, pct } = segmentShare(segments);
  const chartData: ChartDataPoint[] = segments
    .filter((s) => s.value > 0)
    .map((s) => ({
      legend: s.label,
      color: s.color,
      horizontalBarChartdata: { x: s.value, total },
      xAxisCalloutData: s.label,
      yAxisCalloutData: `${s.valueLabel ?? `${s.value}개`} · ${pct(s.value)}%`,
    }));
  return (
    <div className={mergeClasses(styles.stackWrap, styles.chartText)}>
      {total > 0 && (
        <HorizontalBarChart
          data={[{ chartTitle: "", chartData }]}
          chartDataMode="hidden"
          barHeight={12}
          // 건수·비율은 아래 범례에 있다. 막대 위 콜아웃은 같은 숫자를 Hero 크기로 한 번 더 보여 가린다.
          hideTooltip
          // 범례는 아래에서 드릴스루 버튼으로 직접 그린다(Fluent 범례는 강조만 한다).
          styles={{ legendContainer: styles.hidden, chart: styles.clip } as unknown as HorizontalBarChartStyles}
        />
      )}
      <SegmentLegend segments={segments} ariaLabel={ariaLabel} onPick={onPick} pickHint={pickHint} />
    </div>
  );
}

/**
 * 파이 — 전체를 이루는 구성(상태 분포). innerRadius 0이라 도넛이 아니다.
 * 조각은 가운데, 범례는 아래. 조각을 가리키면 건수·비율이 뜨고, 누르면 그 구성으로 이동한다.
 * 0건은 조각에서 빠지고 범례에만 남는다.
 */
export function PieVisual<Key extends string>({ segments, ariaLabel, onPick, height = 180, pickHint = "보기" }: { segments: readonly Segment<Key>[]; ariaLabel: string; onPick?: (key: Key) => void; height?: number; pickHint?: string }) {
  const styles = useStyles();
  const { pct } = segmentShare(segments);
  const pie = Math.max(140, height - 32);
  const chartData: ChartDataPoint[] = segments
    .filter((s) => s.value > 0)
    .map((s) => ({
      legend: s.label,
      data: s.value,
      color: s.color,
      yAxisCalloutData: `${s.valueLabel ?? `${s.value}개`} · ${pct(s.value)}%`,
      onClick: onPick ? () => onPick(s.key) : undefined,
    }));
  return (
    <div className={mergeClasses(styles.pieStack, styles.chartText)} style={{ height }}>
      <div className={styles.pieGrow}>
      <div className={mergeClasses(styles.pieBox, onPick && styles.pieHit)} style={{ width: pie, height: pie }}>
        <DonutChart
          data={{ chartData }}
          innerRadius={0}
          hideLegend
          hideLabels
          width={pie}
          height={pie}
          onRenderCalloutPerDataPoint={(point) =>
            point?.legend ? (
              <span className={styles.callout}>
                {point.legend} {point.yAxisCalloutData ?? point.data}
              </span>
            ) : undefined
          }
        />
      </div>
      </div>
      <SegmentLegend segments={segments} ariaLabel={ariaLabel} onPick={onPick} pickHint={pickHint} center />
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Area stack — 한 색으로 쌓아 줄어드는 양(남은 작업). Fluent에 면적 차트가 없어 여기서 그린다.
 * 띠는 아래가 진하고 위가 옅다. 알약은 합계, 누르면 onPick.
 * ---------------------------------------------------------------------------------------------- */

export interface AreaBand {
  key: string;
  legend: string;
  values: readonly number[];
}

export interface AreaStackVisualProps {
  labels: readonly string[];
  bands: readonly AreaBand[];
  /** 합계 알약을 달 인덱스 */
  markers: readonly number[];
  height: number;
  ariaLabel: string;
  formatValue?: (value: number) => string;
  onPick?: (index: number) => void;
}

function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/** 스택이 서로를 넘지 않게 기울기를 제한하는 단조 3차 곡선. */
function monotoneSlopes(xs: readonly number[], ys: readonly number[]): number[] {
  const n = xs.length;
  const slope = new Array<number>(n).fill(0);
  if (n < 2) return slope;
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1]! - xs[i]!;
    delta.push(dx === 0 ? 0 : (ys[i + 1]! - ys[i]!) / dx);
  }
  slope[0] = delta[0]!;
  slope[n - 1] = delta[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    const left = delta[i - 1]!;
    const right = delta[i]!;
    slope[i] = left === 0 || right === 0 || left * right < 0 ? 0 : (left + right) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (delta[i] === 0) {
      slope[i] = 0;
      slope[i + 1] = 0;
      continue;
    }
    const a = slope[i]! / delta[i]!;
    const b = slope[i + 1]! / delta[i]!;
    const hypot = a * a + b * b;
    if (hypot > 9) {
      const scale = 3 / Math.sqrt(hypot);
      slope[i] = scale * a * delta[i]!;
      slope[i + 1] = scale * b * delta[i]!;
    }
  }
  return slope;
}

function bandPath(xs: readonly number[], top: readonly number[], bottom: readonly number[]): string {
  const n = xs.length;
  if (n === 0) return "";
  const topSlope = monotoneSlopes(xs, top);
  const bottomSlope = monotoneSlopes(xs, bottom);
  let d = `M ${xs[0]} ${top[0]}`;
  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1]! - xs[i]!;
    d += ` C ${xs[i]! + dx / 3} ${top[i]! + (topSlope[i]! * dx) / 3} ${xs[i + 1]! - dx / 3} ${top[i + 1]! - (topSlope[i + 1]! * dx) / 3} ${xs[i + 1]} ${top[i + 1]}`;
  }
  d += ` L ${xs[n - 1]} ${bottom[n - 1]}`;
  for (let i = n - 2; i >= 0; i--) {
    const dx = xs[i + 1]! - xs[i]!;
    d += ` C ${xs[i + 1]! - dx / 3} ${bottom[i + 1]! - (bottomSlope[i + 1]! * dx) / 3} ${xs[i]! + dx / 3} ${bottom[i]! + (bottomSlope[i]! * dx) / 3} ${xs[i]} ${bottom[i]}`;
  }
  return `${d} Z`;
}

function bandAlpha(index: number, count: number): number {
  if (count <= 1) return 0.88;
  return 0.9 - (index / (count - 1)) * 0.62;
}

function stackTotals(bands: readonly AreaBand[]): number[] {
  const count = bands[0]?.values.length ?? 0;
  return Array.from({ length: count }, (_, index) => bands.reduce((sum, band) => sum + (band.values[index] ?? 0), 0));
}

export function AreaStackVisual({ labels, bands, markers, height, ariaLabel, formatValue = (value) => String(Math.round(value)), onPick }: AreaStackVisualProps) {
  const styles = useStyles();
  const plotRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(plotRef);
  const gradientId = useId().replace(/:/g, "");
  const [active, setActive] = useState<number | null>(null);
  const count = bands[0]?.values.length ?? 0;
  const totals = stackTotals(bands);
  const max = Math.max(1, ...totals);
  const padL = 16;
  const padR = 16;
  const padT = 12;
  const padB = 22;
  const plotW = Math.max(1, width - padL - padR);
  const plotH = Math.max(1, height - padT - padB);
  const xs = Array.from({ length: count }, (_, index) => (count <= 1 ? padL + plotW / 2 : padL + (index / (count - 1)) * plotW));
  const yOf = (value: number) => padT + plotH - (value / max) * plotH;
  const baseline = padT + plotH;
  const cumulative: number[][] = [];
  let running = new Array<number>(count).fill(0);
  for (const band of bands) {
    const next = running.map((value, index) => value + (band.values[index] ?? 0));
    cumulative.push(next);
    running = next;
  }
  const nearest = (clientX: number) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || xs.length === 0) return 0;
    const x = clientX - rect.left;
    let best = 0;
    let dist = Infinity;
    xs.forEach((point, index) => {
      const gap = Math.abs(point - x);
      if (gap < dist) {
        dist = gap;
        best = index;
      }
    });
    return best;
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (count === 0) return;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      setActive((current) => {
        const start = current ?? markers[0] ?? 0;
        return Math.min(count - 1, Math.max(0, start + (event.key === "ArrowRight" ? 1 : -1)));
      });
    } else if (event.key === "Enter" && onPick) {
      event.preventDefault();
      onPick(active ?? markers[0] ?? 0);
    }
  };
  const spoken = markers.map((index) => `${labels[index] ?? ""} ${formatValue(totals[index] ?? 0)}`).join(", ");
  const shown = new Set<number>(markers);
  if (active !== null) shown.add(active);
  const pills = [...shown].sort((a, b) => a - b);
  let lastDateX = -Infinity;
  const dateAt = new Set<number>();
  for (const index of markers) {
    const x = xs[index] ?? 0;
    if (x - lastDateX < 52 && index !== markers[0] && index !== markers[markers.length - 1]) continue;
    if (x - lastDateX < 52 && index === markers[markers.length - 1] && dateAt.size > 1) {
      const previous = [...dateAt].at(-1);
      if (previous !== undefined && previous !== markers[0]) dateAt.delete(previous);
    }
    dateAt.add(index);
    lastDateX = x;
  }

  return (
    <div className={styles.areaRoot}>
      <div
        ref={plotRef}
        className={styles.areaPlot}
        style={{ height, cursor: onPick ? "pointer" : undefined }}
        role="group"
        tabIndex={0}
        aria-label={spoken ? `${ariaLabel}. ${spoken}` : ariaLabel}
        onKeyDown={onKeyDown}
        onPointerMove={(event) => setActive(nearest(event.clientX))}
        onPointerLeave={() => setActive(null)}
        onClick={(event) => {
          if (!onPick) return;
          onPick(nearest(event.clientX));
        }}
      >
        {width > 0 && count > 0 && (
          <svg className={styles.areaSvg} width={width} height={height} aria-hidden>
            <defs>
              {bands.map((band, index) => {
                const alpha = bandAlpha(index, bands.length);
                return (
                  <linearGradient key={band.key} id={`${gradientId}-${index}`} x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" stopColor={tokens.colorBrandBackground} stopOpacity={Math.min(1, alpha + 0.08)} />
                    <stop offset="1" stopColor={tokens.colorBrandBackground} stopOpacity={Math.max(0.12, alpha - 0.18)} />
                  </linearGradient>
                );
              })}
            </defs>
            {bands.map((band, index) => {
              const top = (cumulative[index] ?? []).map(yOf);
              const lower = index === 0 ? new Array<number>(count).fill(baseline) : (cumulative[index - 1] ?? []).map(yOf);
              return <path key={band.key} d={bandPath(xs, top, lower)} fill={`url(#${gradientId}-${index})`} />;
            })}
            {totals[0]! > 0 && <rect x={3} y={yOf(totals[0]!)} width={3} height={Math.max(0, baseline - yOf(totals[0]!))} rx={1.5} fill={tokens.colorBrandBackground} />}
            {pills.map((index) => (
              <line key={index} x1={xs[index]} x2={xs[index]} y1={yOf(totals[index] ?? 0)} y2={baseline} stroke={tokens.colorNeutralBackground1} strokeWidth={1.5} />
            ))}
          </svg>
        )}
        {width > 0 &&
          pills.map((index) => {
            const top = yOf(totals[index] ?? 0);
            const mid = Math.min(baseline - 14, Math.max(padT + 12, (top + baseline) / 2));
            return (
              <span key={index} className={styles.areaPill} style={{ left: xs[index], top: mid, borderColor: index === active ? tokens.colorBrandStroke1 : undefined }}>
                {formatValue(totals[index] ?? 0)}
              </span>
            );
          })}
        {width > 0 &&
          [...dateAt].map((index) => (
            <span key={index} className={styles.areaDate} style={{ left: xs[index], top: height - 16 }}>
              {labels[index]}
            </span>
          ))}
      </div>
      <ul className={styles.legend} aria-label="단계">
        {bands.map((band, index) => (
          <li key={band.key}>
            <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: tokens.colorBrandBackground, opacity: bandAlpha(index, bands.length) }} aria-hidden />
              <span>{band.legend}</span>
              <span className={styles.legendValue}>{formatValue(band.values[0] ?? 0)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * StackedColumns — 시점별 구성 누적 막대(리스크·막힘·지연 추이).
 * ---------------------------------------------------------------------------------------------- */

export interface StackedColumnPart {
  key: string;
  legend: string;
  value: number;
  color: string;
  valueLabel?: string;
}

export interface StackedColumnDatum {
  key: string;
  label: string;
  calloutTitle?: string;
  parts: readonly StackedColumnPart[];
  onClick?: () => void;
}

export function StackedColumnsVisual({
  data,
  height,
  ariaLabel,
  formatY = (v) => String(Math.round(v)),
  yMax,
  yTicks,
  rotateLabels = false,
}: {
  data: readonly StackedColumnDatum[];
  height: number;
  ariaLabel: string;
  formatY?: (value: number) => string;
  yMax?: number;
  yTicks?: number[];
  /** 눈금 글자가 겹치면 기울인다. */
  rotateLabels?: boolean;
}) {
  const points: VerticalStackedChartProps[] = data.map((d) => ({
    xAxisPoint: d.label,
    xAxisCalloutData: d.calloutTitle ?? d.label,
    chartData: d.parts.map((p) => ({
      legend: p.legend,
      data: p.value,
      color: p.color,
      yAxisCalloutData: p.valueLabel ?? formatY(p.value),
    })),
  }));
  return (
    <ChartFrame height={height}>
      <VerticalStackedBarChart
        data={points}
        chartTitle={ariaLabel}
        roundCorners
        barGapMax={2}
        barWidth="auto"
        maxBarWidth={28}
        hideLabels
        isCalloutForStack
        yMinValue={0}
        yMaxValue={yMax}
        yAxisTickCount={4}
        yAxisTickValues={yTicks}
        yAxisTickFormat={(v: number) => formatY(v)}
        rotateXAxisLables={rotateLabels}
        margins={{ top: 16, right: 16, bottom: rotateLabels ? 48 : 28, left: 44 }}
        legendProps={{ allowFocusOnLegends: true }}
        onBarClick={(_, item) => {
          const x = "xAxisPoint" in item ? String(item.xAxisPoint) : undefined;
          const hit = x ? data.find((d) => d.label === x) : undefined;
          hit?.onClick?.();
        }}
        svgProps={{ "aria-label": ariaLabel }}
      />
    </ChartFrame>
  );
}

/* ------------------------------------------------------------------------------------------------
 * PhaseProgress — WBS 상위 단계별 계획(해칭/마커) 대비 실제 진척(채움 막대) 비교.
 * ---------------------------------------------------------------------------------------------- */

export interface PhaseProgressDatum {
  key: string;
  wbs: string;
  label: string;
  actual: number;
  planned: number | null;
  color: string;
  actualLabel?: string;
  plannedLabel?: string;
  titleText?: string;
  onClick?: () => void;
}

export function PhaseProgressVisual({
  data,
  ariaLabel,
}: {
  data: readonly PhaseProgressDatum[];
  ariaLabel: string;
}) {
  const styles = useStyles();
  return (
    <div className={styles.bulletRoot} role="list" aria-label={ariaLabel}>
      {data.map((row) => {
        const actual = Math.max(0, Math.min(100, Math.round(row.actual)));
        const planned = row.planned === null ? null : Math.max(0, Math.min(100, Math.round(row.planned)));
        const diff = planned === null ? null : actual - planned;
        const diffText = diff === null ? "" : diff === 0 ? "±0%p" : `${diff > 0 ? "+" : ""}${diff}%p`;
        const title = row.titleText ?? `${row.wbs} ${row.label} · 실제 ${actual}%${planned !== null ? ` / 계획 ${planned}% (${diffText})` : ""}`;
        const inner = (
          <>
            <span className={styles.bulletName} title={title}>
              {row.wbs && <span className={styles.bulletCode}>{row.wbs}</span>}
              <span className={styles.bulletLabel}>{row.label}</span>
            </span>
            <span className={styles.bulletTrack} aria-hidden>
              {planned !== null && planned > actual && (
                <span className={styles.bulletPlanZone} style={{ width: `${planned}%` }} />
              )}
              <span className={styles.bulletFill} style={{ width: `${actual}%`, backgroundColor: row.color }} />
              {planned !== null && (
                <span className={styles.bulletMarker} style={{ left: `calc(${planned}% - 1px)` }} />
              )}
            </span>
            <span className={styles.bulletNums}>
              <span className={styles.bulletActual}>{row.actualLabel ?? `${actual}%`}</span>
              {planned !== null && <span className={styles.bulletPlan}>{row.plannedLabel ?? `/ ${planned}%`}</span>}
            </span>
          </>
        );
        return row.onClick ? (
          <button
            key={row.key}
            type="button"
            role="listitem"
            className={mergeClasses(styles.bulletRow, styles.bulletRowInteractive)}
            onClick={row.onClick}
            aria-label={title}
          >
            {inner}
          </button>
        ) : (
          <div key={row.key} role="listitem" className={styles.bulletRow} aria-label={title}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Heatmap — 요일 × 주처럼 두 축의 건수. 없는 칸(기간 밖)은 빈칸이고 0은 관측된 0이다.
 * 색 단계는 그 그림 안의 최댓값 대비다. 퍼센트를 칸끼리 더하지 않는다.
 * ---------------------------------------------------------------------------------------------- */

export interface HeatmapColumn {
  key: string;
  label: string;
}

export interface HeatmapCell {
  key: string;
  /** null이면 기간 밖 */
  value: number | null;
  label?: string;
  tooltip: string;
  /** 기준일처럼 눈여겨볼 칸 */
  marked?: boolean;
  onClick?: () => void;
}

export interface HeatmapRow {
  key: string;
  label: string;
  cells: readonly HeatmapCell[];
}

function heatLevel(value: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (value <= 0 || max <= 0) return 0;
  const share = value / max;
  if (share <= 0.25) return 1;
  if (share <= 0.5) return 2;
  if (share <= 0.75) return 3;
  return 4;
}

export function HeatmapVisual({
  columns,
  rows,
  peakColumn = null,
  height,
  ariaLabel,
}: {
  columns: readonly HeatmapColumn[];
  rows: readonly HeatmapRow[];
  /** 합이 가장 큰 열. 머리글만 브랜드색 */
  peakColumn?: number | null;
  height: number;
  ariaLabel: string;
}) {
  const styles = useStyles();
  const max = Math.max(0, ...rows.flatMap((row) => row.cells.map((cell) => cell.value ?? 0)));
  return (
    <div className={styles.heatRoot} style={{ height }} role="grid" aria-label={ariaLabel}>
      <div
        className={styles.heatGrid}
        style={{
          height,
          display: "grid",
          gridTemplateColumns: `44px repeat(${columns.length}, minmax(0, 1fr))`,
          gridTemplateRows: `16px repeat(${Math.max(rows.length, 1)}, minmax(0, 1fr))`,
          gap: "3px",
        }}
      >
        <span />
        {columns.map((column, index) => (
          <span key={column.key} className={mergeClasses(styles.heatHead, index === peakColumn && styles.heatHeadOn)} role="columnheader">
            {column.label}
          </span>
        ))}
        {rows.map((row) => (
          <span key={row.key} style={{ display: "contents" }}>
            <span className={styles.heatRowLabel} role="rowheader" title={row.label}>
              {row.label}
            </span>
            {row.cells.map((cell) => {
              const gap = cell.value === null;
              const level = gap ? undefined : heatLevel(cell.value ?? 0, max);
              const className = mergeClasses(styles.heatCell, gap && styles.heatGap, !cell.onClick && styles.heatStatic, cell.marked && styles.heatMark);
              const text = gap ? "—" : (cell.label ?? String(cell.value));
              return cell.onClick ? (
                <button key={cell.key} type="button" role="gridcell" className={className} data-level={level} title={cell.tooltip} aria-label={cell.tooltip} onClick={cell.onClick}>
                  {text}
                </button>
              ) : (
                <span key={cell.key} role="gridcell" className={className} data-level={level} title={cell.tooltip} aria-label={cell.tooltip}>
                  {text}
                </span>
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Phase lanes — 단계가 비는 날. 기준선보다 뒤인 구간만 위험색.
 * ---------------------------------------------------------------------------------------------- */

export interface PhaseLaneDatum {
  key: string;
  label: string;
  detail: string;
  /** 도메인 시작이 0, 끝이 1 */
  start: number;
  end: number;
  /** 막대 안에서 위험 구간이 시작되는 비율. null이면 전부 기준선 앞 */
  lateFrom: number | null;
  onClick?: () => void;
}

export interface PhaseLaneTick {
  /** 0..1 */
  at: number;
  label: string;
  align: "start" | "middle" | "end";
}

export function PhaseLanesVisual({
  lanes,
  referenceAt,
  ticks,
  height,
  ariaLabel,
}: {
  lanes: readonly PhaseLaneDatum[];
  referenceAt: number | null;
  ticks: readonly PhaseLaneTick[];
  height: number;
  ariaLabel: string;
}) {
  const styles = useStyles();
  const late = lanes.some((lane) => lane.lateFrom !== null && lane.lateFrom < 1);
  const early = lanes.some((lane) => lane.lateFrom === null || lane.lateFrom > 0);
  const anchor = (align: PhaseLaneTick["align"]): "0%" | "-50%" | "-100%" => (align === "start" ? "0%" : align === "end" ? "-100%" : "-50%");
  const rows = Math.max(lanes.length, 1);
  const labelRow = referenceAt !== null;
  const laneRow = (index: number) => index + (labelRow ? 2 : 1);
  return (
    <div className={styles.laneRoot} style={{ height }}>
      <div className={styles.laneFrame} style={{ gridTemplateRows: `${labelRow ? "14px " : ""}repeat(${rows}, minmax(22px, 1fr)) 16px` }} role="list" aria-label={ariaLabel}>
        {lanes.map((lane, index) => {
          const width = Math.max(0, lane.end - lane.start);
          const lateFrom = lane.lateFrom;
          const before = lateFrom === null ? 100 : Math.max(0, Math.min(100, lateFrom * 100));
          const track = (
            <span className={styles.laneBar} style={{ left: `${lane.start * 100}%`, width: `${width * 100}%` }}>
              {before > 0 && <span className={styles.laneBefore} style={{ width: `${before}%` }} />}
              {before < 100 && <span className={styles.laneAfter} style={{ width: `${100 - before}%` }} />}
            </span>
          );
          return (
            <span key={lane.key} style={{ display: "contents" }} role="listitem">
              <span className={styles.laneName} style={{ gridColumn: 1, gridRow: laneRow(index) }} title={lane.label}>
                {lane.label}
              </span>
              {lane.onClick ? (
                <button type="button" className={styles.laneTrack} style={{ gridColumn: 2, gridRow: laneRow(index) }} aria-label={`${lane.label}. ${lane.detail}`} onClick={lane.onClick}>
                  {track}
                </button>
              ) : (
                <div className={mergeClasses(styles.laneTrack, styles.laneTrackStatic)} style={{ gridColumn: 2, gridRow: laneRow(index) }} aria-label={`${lane.label}. ${lane.detail}`}>
                  {track}
                </div>
              )}
            </span>
          );
        })}
        {referenceAt !== null && (
          <div className={styles.laneOverlay} style={{ gridColumn: 2, gridRow: `1 / span ${rows + 1}` }} aria-hidden>
            <span className={styles.laneGuide} style={{ left: `${referenceAt * 100}%`, top: "14px" }} />
            <span
              className={styles.laneGuideLabel}
              style={{
                left: `${referenceAt * 100}%`,
                transform: referenceAt > 0.72 ? "translateX(-100%)" : referenceAt < 0.28 ? "translateX(0)" : "translateX(-50%)",
              }}
            >
              기준선
            </span>
          </div>
        )}
        <div className={styles.laneAxis} style={{ gridColumn: 2, gridRow: rows + (labelRow ? 2 : 1) }} aria-hidden>
          {ticks.map((tick) => (
            <span key={`${tick.at}-${tick.label}`} className={styles.laneTick} style={{ left: `${tick.at * 100}%`, transform: `translateX(${anchor(tick.align)})` }}>
              {tick.label}
            </span>
          ))}
        </div>
      </div>
      {(early || late) && referenceAt !== null && (
        <ul className={styles.legend} aria-label="범례">
          {early && (
            <li>
              <span className={styles.legendItem}>
                <span className={styles.legendDot} style={{ backgroundColor: tokens.colorBrandBackground }} aria-hidden />
                <span>기준선까지</span>
              </span>
            </li>
          )}
          {late && (
            <li>
              <span className={styles.legendItem}>
                <span className={styles.legendDot} style={{ backgroundColor: tokens.colorPaletteRedBackground3 }} aria-hidden />
                <span>기준선 이후</span>
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

